/**
 * ------------------------------------------------------------
 * Minicart subscriptions
 * ------------------------------------------------------------
 *
 * BASE:
 *
 * .minicart__item-wrapper[data-item-key]
 * ├── .minicart__item.js-minicart-item-list
 * └── .minicart__subscription
 *
 * IMPORTANT:
 *
 * Normal subscription flow:
 *   - Toggle changes the REAL selling plan.
 *   - 10% / 20% remains real in cart.
 *
 * 25% flow:
 *   - 25% is VISUAL ONLY while the cart qualifies.
 *   - Prices/copy/badge/savings are simulated using the real
 *     25% selling plan allocation.
 *   - The actual cart remains on its current selling plan.
 *   - ONLY when checkout is clicked do we convert eligible
 *     lines to the real 25% selling plan.
 *
 * CONDITION:
 *
 *   subscription quantity >= 2
 *
 * AND:
 *
 *   the individual subscribed product must have a 25% selling plan.
 *
 * ------------------------------------------------------------
 */

(() => {
  "use strict";

  const PRODUCT_CACHE = new Map();

  const EXCLUDED_25_PRODUCT_IDS = new Set([
    "15028231012720",
    "14646572253552",
    "9395489734971",
    "8775200833851",
    "14967354196336",
  ]);

  let syncTimer = null;
  let syncRequest = 0;

  let checkoutPreparing = false;
  let normalizationInFlight = false;

  /**
   * ----------------------------------------------------------
   * Helpers
   * ----------------------------------------------------------
   */

  const getRoot = () => {
    return window.Shopify?.routes?.root || "/";
  };

  const getMoney = (cents) => {
    const value = Number(cents) || 0;

    if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
      return Shopify.formatMoney(value, "${{amount}}").replace(/\.00$/, "");
    }

    return `$${(value / 100).toFixed(2)}`;
  };

  const escapeHTML = (value) => {
    const div = document.createElement("div");

    div.textContent = value == null ? "" : String(value);

    return div.innerHTML;
  };

  const getProductHandle = (item) => {
    if (!item?.url) {
      return null;
    }

    try {
      const url = new URL(item.url, window.location.origin);

      const match = url.pathname.match(/\/products\/([^/]+)/);

      return match ? decodeURIComponent(match[1]) : null;
    } catch {
      return null;
    }
  };

  const getCadenceFromPlan = (plan) => {
    if (!plan) {
      return "";
    }

    const name = plan.name || "";

    const nameMatch = name.match(/(\d+)\s*days?/i);

    if (nameMatch) {
      return `${nameMatch[1]} days`;
    }

    if (Array.isArray(plan.options)) {
      const frequencyOption = plan.options.find((option) =>
        option?.name?.toLowerCase().includes("frequency"),
      );

      if (frequencyOption?.value) {
        const optionMatch = frequencyOption.value.match(/(\d+)\s*-?\s*day/i);

        if (optionMatch) {
          return `${optionMatch[1]} days`;
        }
      }
    }

    return name;
  };

  const getSavingPercentage = (plan) => {
    if (!plan?.price_adjustments?.length) {
      return null;
    }

    const adjustment = plan.price_adjustments[0];

    if (adjustment.value_type !== "percentage") {
      return null;
    }

    return Math.abs(Number(adjustment.value) || 0);
  };

  const isExcludedPlan = (plan) => {
    return Number(plan?.savingPercentage) === 25;
  };

  const getSelectablePlans = (plans = []) => {
    return plans.filter((plan) => !isExcludedPlan(plan));
  };

  const getPlanById = (product, planId) => {
    if (!product?.selling_plan_groups?.length) {
      return null;
    }

    for (const group of product.selling_plan_groups) {
      const plan = group.selling_plans?.find(
        (sellingPlan) => String(sellingPlan.id) === String(planId),
      );

      if (plan) {
        return plan;
      }
    }

    return null;
  };

  const get25Plan = (subscriptionData) => {
    if (!subscriptionData?.plans?.length) {
      return null;
    }

    return (
      subscriptionData.plans.find(
        (plan) => Number(plan.savingPercentage) === 25,
      ) || null
    );
  };

  const isProductExcludedFrom25 = (subscriptionData) => {
    const productId = subscriptionData?.product?.id;

    if (!productId) {
      return false;
    }

    return EXCLUDED_25_PRODUCT_IDS.has(String(productId));
  };

  const getCurrentPlan = (item, subscriptionData) => {
    if (!item?.selling_plan_allocation) {
      return null;
    }

    const currentPlanId = item.selling_plan_allocation.selling_plan?.id;

    if (!currentPlanId) {
      return null;
    }

    return (
      subscriptionData?.plans?.find(
        (plan) => String(plan.id) === String(currentPlanId),
      ) || null
    );
  };

  const getActualSavingPercentage = (item, currentPlan) => {
    if (!item?.selling_plan_allocation) {
      return 0;
    }

    if (currentPlan?.savingPercentage != null) {
      return Number(currentPlan.savingPercentage) || 0;
    }

    const compareAt =
      Number(item.selling_plan_allocation.compare_at_price) ||
      Number(item.price) ||
      0;

    const price =
      Number(item.selling_plan_allocation.price) || Number(item.price) || 0;

    if (!compareAt || !price || price >= compareAt) {
      return 0;
    }

    return Math.round(((compareAt - price) / compareAt) * 100);
  };

  /**
   * ----------------------------------------------------------
   * Product data
   * ----------------------------------------------------------
   */

  const getProductData = async (item) => {
    const handle = getProductHandle(item);

    if (!handle) {
      return null;
    }

    if (PRODUCT_CACHE.has(handle)) {
      return PRODUCT_CACHE.get(handle);
    }

    const request = fetch(
      `${getRoot()}products/${encodeURIComponent(handle)}.js`,
      {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
        },
      },
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Product request failed");
        }

        return response.json();
      })
      .then((product) => {
        PRODUCT_CACHE.set(handle, product);

        return product;
      })
      .catch(() => {
        PRODUCT_CACHE.delete(handle);

        return null;
      });

    PRODUCT_CACHE.set(handle, request);

    return request;
  };

  /**
   * ----------------------------------------------------------
   * Subscription data
   * ----------------------------------------------------------
   */

  const getSubscriptionData = async (item) => {
    const product = await getProductData(item);

    if (!product) {
      return null;
    }

    const variant = product.variants?.find(
      (productVariant) => String(productVariant.id) === String(item.id),
    );

    if (!variant) {
      return null;
    }

    const allocations = variant.selling_plan_allocations || [];

    if (!allocations.length) {
      return null;
    }

    const plans = [];

    allocations.forEach((allocation) => {
      const plan = getPlanById(product, allocation.selling_plan_id);

      if (!plan) {
        return;
      }

      plans.push({
        id: allocation.selling_plan_id,

        name: plan.name,

        cadence: getCadenceFromPlan(plan),

        price: Number(allocation.price),

        compareAtPrice: Number(allocation.compare_at_price || 0),

        perDeliveryPrice: Number(
          allocation.per_delivery_price || allocation.price,
        ),

        savingPercentage: getSavingPercentage(plan),

        plan,
      });
    });

    if (!plans.length) {
      return null;
    }

    return {
      product,
      variant,
      plans,
    };
  };

  /**
   * ----------------------------------------------------------
   * 25% eligibility
   * ----------------------------------------------------------
   *
   * Count REAL subscription units.
   *
   * Example:
   *
   * Product A subscription x1
   * Product B subscription x1
   *
   * => total = 2
   *
   * Product B can visually receive 25% IF it has a 25%
   * selling plan.
   *
   * Product A does NOT receive 25% if its product has
   * no 25% selling plan.
   */

  const getSubscriptionUnitCount = (cartItems = []) => {
    return cartItems.reduce((total, item) => {
      if (!item?.selling_plan_allocation) {
        return total;
      }

      return total + (Number(item.quantity) || 0);
    }, 0);
  };

  const qualifiesFor25 = (item, subscriptionData, cartItems) => {
    if (!item?.selling_plan_allocation) {
      return false;
    }

    if (isProductExcludedFrom25(subscriptionData)) {
      return false;
    }

    const totalSubscriptionUnits = getSubscriptionUnitCount(cartItems);

    if (totalSubscriptionUnits < 2) {
      return false;
    }

    const plan25 = get25Plan(subscriptionData);

    if (!plan25) {
      return false;
    }

    return true;
  };

  /**
   * ----------------------------------------------------------
   * Display plan
   * ----------------------------------------------------------
   *
   * IMPORTANT:
   *
   * actualPlan:
   *   what Shopify currently has.
   *
   * displayPlan:
   *   what the customer sees.
   *
   * When 25% qualifies:
   *
   * actualPlan = 20%
   * displayPlan = 25%
   *
   * Shopify still has 20%.
   */

  const getDisplayPlan = (item, subscriptionData, cartItems) => {
    const actualPlan = getCurrentPlan(item, subscriptionData);

    if (!actualPlan) {
      return null;
    }

    const plan25 = get25Plan(subscriptionData);

    const shouldSimulate25 = qualifiesFor25(item, subscriptionData, cartItems);

    if (shouldSimulate25 && plan25) {
      return plan25;
    }

    return actualPlan;
  };

  /**
   * ----------------------------------------------------------
   * Effective display values
   * ----------------------------------------------------------
   */

  const getEffectiveCompareAtPrice = (item, displayPlan) => {
    return (
      Number(displayPlan?.compareAtPrice) ||
      Number(item.selling_plan_allocation?.compare_at_price) ||
      Number(item.price) ||
      0
    );
  };

  const getEffectiveSubscriptionPrice = (item, displayPlan) => {
    return (
      Number(displayPlan?.price) ||
      Number(item.selling_plan_allocation?.price) ||
      Number(item.price) ||
      0
    );
  };

  const getEffectiveSavingPercentage = (item, displayPlan) => {
    if (!item?.selling_plan_allocation) {
      return 0;
    }

    if (displayPlan?.savingPercentage != null) {
      return Number(displayPlan.savingPercentage) || 0;
    }

    return getActualSavingPercentage(item, displayPlan);
  };

  const getEffectiveSavingAmount = (item, displayPlan) => {
    const compareAt = getEffectiveCompareAtPrice(item, displayPlan);

    const price = getEffectiveSubscriptionPrice(item, displayPlan);

    return Math.max(compareAt - price, 0);
  };

  /**
   * ----------------------------------------------------------
   * One-time HTML
   * ----------------------------------------------------------
   */

  const renderOneTime = (item, subscriptionData) => {
    const selectablePlans = getSelectablePlans(subscriptionData.plans);

    const firstPlan = selectablePlans[0];

    if (!firstPlan) {
      return "";
    }

    const oneTimePrice = Number(item.price) || 0;

    const subscriptionPrice = Number(firstPlan.price) || 0;

    const savingAmount = Math.max(oneTimePrice - subscriptionPrice, 0);

    const savingPercentage =
      firstPlan.savingPercentage ??
      (oneTimePrice > 0 ? Math.round((savingAmount / oneTimePrice) * 100) : 0);

    const cadence = firstPlan.cadence || firstPlan.name;

    return `
      <div
        class="minicart__subscription"
        data-minicart-subscription
        data-item-key="${escapeHTML(item.key)}"
        data-variant-id="${escapeHTML(item.id)}"
        data-subscription-state="one-time"
      >

        <div class="minicart__subscription-offers">

          <button
            type="button"
            class="minicart__subscription-option is-selected"
            data-subscription-action="one-time"
          >

            <span
              class="minicart__subscription-radio is-selected"
              aria-hidden="true"
            ></span>

            <span
              class="minicart__subscription-option-label"
            >
              ONE-TIME PURCHASE
            </span>

            <span
              class="minicart__subscription-option-price"
            >
              ${getMoney(oneTimePrice)}
            </span>

          </button>

          <button
            type="button"
            class="minicart__subscription-option"
            data-subscription-action="subscribe"
            data-selling-plan-id="${escapeHTML(firstPlan.id)}"
          >

            <span
              class="minicart__subscription-radio"
              aria-hidden="true"
            ></span>

            <span
              class="minicart__subscription-option-content"
            >

              <span
                class="minicart__subscription-option-head"
              >

                <span
                  class="minicart__subscription-option-label"
                >
                  SUBSCRIBE AND SAVE
                  ${savingPercentage}%
                </span>

                <span
                  class="minicart__subscription-option-price"
                >
                  ${getMoney(subscriptionPrice)}
                </span>

              </span>

              <span
                class="minicart__subscription-option-copy"
              >
                Get this for
                ${getMoney(subscriptionPrice)}
                with free shipping,
                delivered every
                ${escapeHTML(cadence)}.
                Skip or cancel anytime.
              </span>

            </span>

          </button>

        </div>

      </div>
    `;
  };

  /**
   * ----------------------------------------------------------
   * Subscription HTML
   * ----------------------------------------------------------
   *
   * actualPlan controls the selector.
   *
   * displayPlan controls:
   *   - percentage
   *   - displayed price
   *   - displayed saving
   *
   * This is critical for simulated 25%.
   */

  const renderSubscription = (
    item,
    subscriptionData,
    actualPlan,
    displayPlan,
  ) => {
    if (!actualPlan) {
      return renderOneTime(item, subscriptionData);
    }

    const currentPrice = getEffectiveSubscriptionPrice(item, displayPlan);

    const compareAtPrice = getEffectiveCompareAtPrice(item, displayPlan);

    const savingPercentage = getEffectiveSavingPercentage(item, displayPlan);

    const savingAmount = Math.max(compareAtPrice - currentPrice, 0);

    const quantity = Number(item.quantity) || 1;

    const selectablePlans = getSelectablePlans(subscriptionData.plans);

    /**
     * The selector must remain tied to the
     * ACTUAL plan.
     *
     * Therefore a simulated 25% plan is NOT
     * added to the selector.
     */

    const cadenceOptions = selectablePlans
      .map(
        (plan) => `
            <option
              value="${escapeHTML(plan.id)}"
              ${String(plan.id) === String(actualPlan.id) ? "selected" : ""}
            >
              ${escapeHTML(plan.cadence || plan.name)}
            </option>
          `,
      )
      .join("");

    const currentCadence = actualPlan.cadence || actualPlan.name || "";

    const mobileCadenceOptions = selectablePlans
      .map(
        (plan) => `
            <button
              type="button"
              class="minicart__subscription-mobile-option${
                String(plan.id) === String(actualPlan.id) ? " is-selected" : ""
              }"
              data-mobile-selling-plan="${escapeHTML(plan.id)}"
              role="option"
              aria-selected="${String(plan.id) === String(actualPlan.id)}"
            >
              ${escapeHTML(plan.cadence || plan.name)}
            </button>
          `,
      )
      .join("");

    return `
      <div
        class="minicart__subscription"
        data-minicart-subscription
        data-item-key="${escapeHTML(item.key)}"
        data-variant-id="${escapeHTML(item.id)}"
        data-subscription-state="subscribe"
      >

        <div
          class="minicart__subscription-subscription-state"
        >

          <div
            class="minicart__subscription-confirmation"
            data-subscription-confirmation
          >

            <span
              class="minicart__subscription-confirmation-label"
            >
              <span aria-hidden="true">✓</span>
              SUBSCRIPTION APPLIED · SAVE
              ${savingPercentage}%
            </span>

            <button
              type="button"
              class="minicart__subscription-undo"
              data-subscription-action="one-time"
            >
              Undo
            </button>

          </div>

          ${
            quantity > 0
              ? `
                <p
                  class="minicart__subscription-nudge"
                >
                  Add one more and save
                  ${getMoney(savingAmount)}
                  on this order.
                </p>
              `
              : ""
          }

          <div
            class="minicart__subscription-plan"
          >

            <span
              class="minicart__subscription-plan-label"
            >
              Delivers every
            </span>

            <!-- DESKTOP -->

            <select
              id="minicart-subscription-cadence-${escapeHTML(item.key)}"
              name="selling_plan"
              class="minicart__subscription-cadence"
              data-subscription-cadence
              aria-label="Delivery frequency"
            >
              ${cadenceOptions}
            </select>

            <!-- MOBILE -->

            <div
              class="minicart__subscription-mobile-select"
              data-mobile-subscription-select
            >

              <button
                type="button"
                class="minicart__subscription-mobile-trigger"
                data-mobile-subscription-trigger
                aria-haspopup="listbox"
                aria-expanded="false"
                aria-label="Delivery frequency"
              >

                <span
                  data-mobile-subscription-value
                >
                  ${escapeHTML(currentCadence)}
                </span>

                <span
                  class="minicart__subscription-mobile-arrow"
                  aria-hidden="true"
                ></span>

              </button>

              <div
                class="minicart__subscription-mobile-dropdown"
                data-mobile-subscription-dropdown
                role="listbox"
                hidden
              >
                ${mobileCadenceOptions}
              </div>

            </div>

            <button
              type="button"
              class="minicart__subscription-switch"
              data-subscription-action="one-time"
            >
              switch to one-time
            </button>

          </div>

        </div>

      </div>
    `;
  };

  /**
   * ----------------------------------------------------------
   * Confirmation
   * ----------------------------------------------------------
   */

  const scheduleConfirmationHide = (component) => {
    if (!component) {
      return;
    }

    const confirmation = component.querySelector(
      "[data-subscription-confirmation]",
    );

    if (!confirmation) {
      return;
    }

    if (component._subscriptionConfirmationTimer) {
      clearTimeout(component._subscriptionConfirmationTimer);
    }

    confirmation.classList.remove("hidden");

    component._subscriptionConfirmationTimer = setTimeout(() => {
      const currentConfirmation = component.querySelector(
        "[data-subscription-confirmation]",
      );

      if (currentConfirmation) {
        currentConfirmation.classList.add("hidden");
      }

      component._subscriptionConfirmationTimer = null;
    }, 6000);
  };

  /**
   * ----------------------------------------------------------
   * Render subscription component
   * ----------------------------------------------------------
   */

  const renderSubscriptionComponent = (
    wrapper,
    item,
    subscriptionData,
    cartItems,
  ) => {
    if (!wrapper || !item || !subscriptionData) {
      return;
    }

    /**
     * --------------------------------------------------------
     * ONE-TIME
     * --------------------------------------------------------
     *
     * The product supports subscriptions, but this specific
     * cart line is currently one-time.
     *
     * IMPORTANT:
     *
     * Do NOT treat this as "no subscription support".
     *
     * We need to render the subscription toggle so the customer
     * can switch this line to a real selling plan.
     */

    const actualPlan = getCurrentPlan(item, subscriptionData);

    if (!actualPlan) {
      const selectablePlans = getSelectablePlans(subscriptionData.plans);

      /**
       * Product has no selectable subscription plans.
       * Nothing to render.
       */

      if (!selectablePlans.length) {
        const existing = wrapper.querySelector("[data-minicart-subscription]");

        if (existing) {
          if (existing._subscriptionConfirmationTimer) {
            clearTimeout(existing._subscriptionConfirmationTimer);
          }

          existing.remove();
        }

        hideOneTimeVisuals(wrapper);

        return;
      }

      /**
       * Product is ONE-TIME but subscription is available.
       *
       * Render the toggle.
       */

      const html = renderOneTime(item, subscriptionData);

      if (!html) {
        return;
      }

      let component = wrapper.querySelector("[data-minicart-subscription]");

      if (!component) {
        wrapper.insertAdjacentHTML("beforeend", html);

        component = wrapper.querySelector("[data-minicart-subscription]");
      } else {
        if (component._subscriptionConfirmationTimer) {
          clearTimeout(component._subscriptionConfirmationTimer);
        }

        component.outerHTML = html;

        component = wrapper.querySelector("[data-minicart-subscription]");
      }

      if (component) {
        component.setAttribute("data-render-state", "one-time");

        component.removeAttribute("data-simulated-25");
      }

      hideOneTimeVisuals(wrapper);

      return;
    }

    /**
     * --------------------------------------------------------
     * ACTIVE SUBSCRIPTION
     * --------------------------------------------------------
     */

    const displayPlan = getDisplayPlan(item, subscriptionData, cartItems);

    if (!displayPlan) {
      return;
    }

    const isSimulated25 =
      String(displayPlan.id) !== String(actualPlan.id) &&
      Number(displayPlan.savingPercentage) === 25;

    const html = renderSubscription(
      item,
      subscriptionData,
      actualPlan,
      displayPlan,
    );

    if (!html) {
      return;
    }

    const state = `subscribe-${actualPlan.id}` + `-display-${displayPlan.id}`;

    let component = wrapper.querySelector("[data-minicart-subscription]");

    const previousState = component?.getAttribute("data-render-state");

    if (component && previousState === state) {
      component.setAttribute("data-item-key", item.key);
      component.setAttribute("data-variant-id", item.id);

      updateLineVisuals(wrapper, item, subscriptionData, cartItems);

      return;
    }

    if (!component) {
      wrapper.insertAdjacentHTML("beforeend", html);

      component = wrapper.querySelector("[data-minicart-subscription]");
    } else {
      if (component._subscriptionConfirmationTimer) {
        clearTimeout(component._subscriptionConfirmationTimer);
      }

      component.outerHTML = html;

      component = wrapper.querySelector("[data-minicart-subscription]");
    }

    component?.setAttribute("data-render-state", state);

    if (isSimulated25) {
      component?.setAttribute("data-simulated-25", "true");
    } else {
      component?.removeAttribute("data-simulated-25");
    }

    scheduleConfirmationHide(component);

    updateLineVisuals(wrapper, item, subscriptionData, cartItems);
  };

  /**
   * ----------------------------------------------------------
   * Update card visual data
   * ----------------------------------------------------------
   *
   * This is where the Vue-generated price is synchronized
   * with the simulated 25% plan.
   */

  const updateLineVisuals = (wrapper, item, subscriptionData, cartItems) => {
    if (!wrapper || !item) {
      return;
    }

    const component = wrapper.querySelector("[data-minicart-subscription]");

    /**
     * No subscription component.
     */

    if (!component) {
      return;
    }

    const actualPlan = getCurrentPlan(item, subscriptionData);

    if (!actualPlan) {
      return;
    }

    const displayPlan = getDisplayPlan(item, subscriptionData, cartItems);

    if (!displayPlan) {
      return;
    }

    const savingPercentage = getEffectiveSavingPercentage(item, displayPlan);

    const displayPrice = getEffectiveSubscriptionPrice(item, displayPlan);

    const compareAtPrice = getEffectiveCompareAtPrice(item, displayPlan);

    const is25 = Number(displayPlan.savingPercentage) === 25;

    /**
     * --------------------------------------------------------
     * PRICE
     * --------------------------------------------------------
     */

    const priceElement = wrapper.querySelector(
      ".minicart__item-row--price .minicart__value",
    );

    if (priceElement && String(item.id) !== "67752821817712") {
      let html = getMoney(displayPrice);

      if (compareAtPrice > displayPrice) {
        html += ` <s>${getMoney(compareAtPrice)}</s>`;
      }

      priceElement.innerHTML = html;
    }

    /**
     * --------------------------------------------------------
     * BADGE
     * --------------------------------------------------------
     *
     * IMPORTANT:
     *
     * .minicart__sub-badge
     * must NOT remain hidden for active subscriptions.
     *
     * .minicart__sub-value
     * must KEEP hidden.
     */

    const badge = wrapper.querySelector(".minicart__sub-badge");

    if (badge) {
      badge.textContent = `SUBSCRIPTION · Save ${savingPercentage}%`;

      badge.classList.remove("hidden");

      badge.setAttribute("data-effective-saving", String(savingPercentage));

      badge.setAttribute("data-simulated-25", String(is25));
    }

    const subValue = wrapper.querySelector(".minicart__sub-value");

    if (subValue) {
      /**
       * DO NOT remove hidden here.
       */
      subValue.classList.add("hidden");
    }

    /**
     * --------------------------------------------------------
     * Subscription option copy
     * --------------------------------------------------------
     */

    const optionLabel = component.querySelector(
      ".minicart__subscription-confirmation-label",
    );

    if (optionLabel) {
      optionLabel.innerHTML =
        `<span aria-hidden="true">✓</span>` +
        ` SUBSCRIPTION APPLIED · SAVE ${savingPercentage}%`;
    }

    const nudge = component.querySelector(".minicart__subscription-nudge");

    if (nudge) {
      const savingAmount = getEffectiveSavingAmount(item, displayPlan);

      nudge.textContent = `Add one more and save ${getMoney(
        savingAmount,
      )} on this order.`;
    }
  };

  /**
   * ----------------------------------------------------------
   * Hide subscription badge for one-time lines
   * ----------------------------------------------------------
   */

  const hideOneTimeVisuals = (wrapper) => {
    if (!wrapper) {
      return;
    }

    const badge = wrapper.querySelector(".minicart__sub-badge");

    if (badge) {
      badge.classList.add("hidden");
    }

    const subValue = wrapper.querySelector(".minicart__sub-value");

    if (subValue) {
      subValue.classList.add("hidden");
    }
  };

  /**
   * ----------------------------------------------------------
   * Subscription savings total
   * ----------------------------------------------------------
   *
   * IMPORTANT:
   *
   * This MUST use the same effective plan used by
   * updateLineVisuals().
   *
   * Therefore:
   *
   * 20% actual:
   *   $89 -> $71.20
   *
   * simulated 25%:
   *   $89 -> $66.75
   *
   * savings:
   *   $22.25
   *
   * ----------------------------------------------------------
   */

  const updateSubscriptionSavings = async (cartItems) => {
    const container = document.querySelector(
      ".js-minicart-total-subscription-savings",
    );

    const amountElement = document.querySelector(
      ".minicart__total-subscription-saving-amount",
    );

    if (!container || !amountElement) {
      return;
    }

    const activeSubscriptionItems = cartItems.filter(
      (item) => !!item?.selling_plan_allocation,
    );

    /**
     * No subscriptions:
     * hide the entire component.
     */

    if (!activeSubscriptionItems.length) {
      container.classList.add("hidden");

      amountElement.textContent = getMoney(0);

      return;
    }

    let totalSavings = 0;

    /**
     * Load product data in parallel.
     */

    const subscriptionDataList = await Promise.all(
      activeSubscriptionItems.map((item) => getSubscriptionData(item)),
    );

    activeSubscriptionItems.forEach((item, index) => {
      const subscriptionData = subscriptionDataList[index];

      if (!subscriptionData) {
        return;
      }

      const displayPlan = getDisplayPlan(item, subscriptionData, cartItems);

      if (!displayPlan) {
        return;
      }

      const savingPerUnit = getEffectiveSavingAmount(item, displayPlan);

      const quantity = Number(item.quantity) || 0;

      totalSavings += savingPerUnit * quantity;
    });

    /**
     * Show only if there is actually savings.
     */

    if (totalSavings > 0) {
      container.classList.remove("hidden");

      amountElement.textContent = getMoney(totalSavings);
    } else {
      container.classList.add("hidden");

      amountElement.textContent = getMoney(0);
    }
  };

  /**
   * ----------------------------------------------------------
   * Find cart item for wrapper
   * ----------------------------------------------------------
   *
   * Exact line key first.
   *
   * Fallback:
   * variant + subscription state.
   *
   * This avoids the "Cart line not found" problem when
   * Vue has already regenerated the DOM/key.
   */

  const findCartItemForWrapper = (wrapper, cartItems) => {
    if (!wrapper) {
      return null;
    }

    const wrapperKey = wrapper.getAttribute("data-item-key");

    if (wrapperKey) {
      const exact = cartItems.find(
        (item) => String(item.key) === String(wrapperKey),
      );

      if (exact) {
        return exact;
      }
    }

    const component = wrapper.querySelector("[data-minicart-subscription]");

    const variantId = component?.getAttribute("data-variant-id");

    if (!variantId) {
      return null;
    }

    const state = component.getAttribute("data-subscription-state");

    /**
     * Prefer matching subscription state.
     */

    if (state === "subscribe") {
      const subscriptionItem = cartItems.find(
        (item) =>
          String(item.variant_id) === String(variantId) &&
          !!item.selling_plan_allocation,
      );

      if (subscriptionItem) {
        return subscriptionItem;
      }
    }

    if (state === "one-time") {
      const oneTimeItem = cartItems.find(
        (item) =>
          String(item.variant_id) === String(variantId) &&
          !item.selling_plan_allocation,
      );

      if (oneTimeItem) {
        return oneTimeItem;
      }
    }

    /**
     * Last fallback.
     */

    return (
      cartItems.find((item) => String(item.variant_id) === String(variantId)) ||
      null
    );
  };

  /**
   * ----------------------------------------------------------
   * Normalize expired real 25% plans
   * ----------------------------------------------------------
   *
   * Normally 25% is only simulated.
   *
   * But if the customer has already clicked checkout and
   * comes back, the real cart can contain the 25% plan.
   *
   * If the cart no longer qualifies, move it back to the
   * first selectable plan.
   */

  const normalizeExpired25Plans = async (cartItems, subscriptionDataByKey) => {
    if (normalizationInFlight) {
      return;
    }

    const totalSubscriptionUnits = getSubscriptionUnitCount(cartItems);

    /**
     * If we still have 2+ subscription units,
     * nothing to normalize.
     */

    if (totalSubscriptionUnits >= 2) {
      return;
    }

    const expired25Items = cartItems.filter((item) => {
      if (!item?.selling_plan_allocation) {
        return false;
      }

      const currentPlanId = item.selling_plan_allocation.selling_plan?.id;

      if (!currentPlanId) {
        return false;
      }

      const subscriptionData = subscriptionDataByKey.get(String(item.key));

      if (!subscriptionData) {
        return false;
      }

      const currentPlan = getCurrentPlan(item, subscriptionData);

      return Number(currentPlan?.savingPercentage) === 25;
    });

    if (!expired25Items.length) {
      return;
    }

    normalizationInFlight = true;

    try {
      for (const item of expired25Items) {
        const subscriptionData = subscriptionDataByKey.get(String(item.key));

        if (!subscriptionData) {
          continue;
        }

        const selectablePlans = getSelectablePlans(subscriptionData.plans);

        const fallbackPlan = selectablePlans[0];

        if (!fallbackPlan) {
          continue;
        }

        try {
          const response = await fetch(`${getRoot()}cart/change.js`, {
            method: "POST",

            credentials: "same-origin",

            headers: {
              "Content-Type": "application/json",

              Accept: "application/json",
            },

            body: JSON.stringify({
              id: item.key,

              quantity: item.quantity,

              selling_plan: Number(fallbackPlan.id),
            }),
          });

          if (!response.ok) {
            continue;
          }

          /**
           * Use returned cart for next line.
           */

          const updatedCart = await response.json();

          cartItems = updatedCart?.items || cartItems;
        } catch (error) {
          console.warn("[Minicart subscriptions] normalize 25% error", error);
        }
      }

      /**
       * Tell the existing minicart/store to refresh.
       */

      if (
        window.VueMinicart &&
        window.VueMinicart.config?.globalProperties?.$store
      ) {
        try {
          await window.VueMinicart.config.globalProperties.$store.dispatch(
            "initCart",
          );
        } catch {
          scheduleSubscriptionSync();
        }
      } else {
        scheduleSubscriptionSync();
      }
    } finally {
      normalizationInFlight = false;
    }
  };

  /**
   * ----------------------------------------------------------
   * Sync minicart
   * ----------------------------------------------------------
   */

  const syncMinicartSubscriptions = async () => {
    const requestId = ++syncRequest;

    const wrappers = [
      ...document.querySelectorAll(".minicart__item-wrapper[data-item-key]"),
    ];

    if (!wrappers.length) {
      return;
    }

    let cart;

    try {
      const response = await fetch(`${getRoot()}cart.js`, {
        credentials: "same-origin",

        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        return;
      }

      cart = await response.json();
    } catch {
      return;
    }

    if (requestId !== syncRequest) {
      return;
    }

    let cartItems = cart?.items || [];

    /**
     * ------------------------------------------------------
     * Product data in parallel
     * ------------------------------------------------------
     */

    const itemDataPairs = await Promise.all(
      wrappers.map(async (wrapper) => {
        const item = findCartItemForWrapper(wrapper, cartItems);

        if (!item) {
          return {
            wrapper,
            item: null,
            subscriptionData: null,
          };
        }

        const subscriptionData = await getSubscriptionData(item);

        return {
          wrapper,
          item,
          subscriptionData,
        };
      }),
    );

    if (requestId !== syncRequest) {
      return;
    }

    /**
     * Map data by current line key.
     */

    const subscriptionDataByKey = new Map();

    itemDataPairs.forEach(({ item, subscriptionData }) => {
      if (item && subscriptionData) {
        subscriptionDataByKey.set(String(item.key), subscriptionData);
      }
    });

    /**
     * Update total savings first.
     *
     * This uses exactly the same effective
     * 25% logic as the card.
     */

    await updateSubscriptionSavings(cartItems);

    if (requestId !== syncRequest) {
      return;
    }

    /**
     * Render/update each line.
     */

    itemDataPairs.forEach(({ wrapper, item, subscriptionData }) => {
      if (!wrapper) {
        return;
      }

      if (!item) {
        const component = wrapper.querySelector("[data-minicart-subscription]");

        if (component?._subscriptionConfirmationTimer) {
          clearTimeout(component._subscriptionConfirmationTimer);
        }

        component?.remove();

        hideOneTimeVisuals(wrapper);

        return;
      }

      /**
       * Keep exact Shopify key.
       */

      if (String(wrapper.getAttribute("data-item-key")) !== String(item.key)) {
        wrapper.setAttribute("data-item-key", item.key);
      }

      /**
       * Product doesn't support subscriptions.
       */

      if (!subscriptionData) {
        const component = wrapper.querySelector("[data-minicart-subscription]");

        if (component?._subscriptionConfirmationTimer) {
          clearTimeout(component._subscriptionConfirmationTimer);
        }

        component?.remove();

        hideOneTimeVisuals(wrapper);

        return;
      }

      /**
       * Active subscription.
       */

      if (item.selling_plan_allocation) {
        renderSubscriptionComponent(wrapper, item, subscriptionData, cartItems);

        return;
      }

      /**
       * One-time.
       */

      renderSubscriptionComponent(wrapper, item, subscriptionData, cartItems);

      hideOneTimeVisuals(wrapper);
    });

    /**
     * ------------------------------------------------------
     * Normalize an actual 25% plan if condition disappeared.
     * ------------------------------------------------------
     */

    await normalizeExpired25Plans(cartItems, subscriptionDataByKey);
  };

  /**
   * ----------------------------------------------------------
   * Schedule sync
   * ----------------------------------------------------------
   */

  const scheduleSubscriptionSync = (delay = 150) => {
    clearTimeout(syncTimer);

    syncTimer = setTimeout(() => {
      syncMinicartSubscriptions();
    }, delay);
  };

  /**
   * ----------------------------------------------------------
   * Find latest cart line
   * ----------------------------------------------------------
   */

  const getLatestCart = async () => {
    const response = await fetch(`${getRoot()}cart.js`, {
      credentials: "same-origin",

      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Unable to fetch cart");
    }

    return response.json();
  };

  const findCurrentCartItem = (cart, itemKey, component) => {
    if (!cart?.items?.length) {
      return null;
    }

    /**
     * Exact key first.
     */

    const exact = cart.items.find(
      (item) => String(item.key) === String(itemKey),
    );

    if (exact) {
      return exact;
    }

    /**
     * Component fallback.
     */

    if (component) {
      const variantId = component.getAttribute("data-variant-id");

      if (variantId) {
        const state = component.getAttribute("data-subscription-state");

        if (state === "subscribe") {
          const subscriptionItem = cart.items.find(
            (item) =>
              String(item.variant_id) === String(variantId) &&
              !!item.selling_plan_allocation,
          );

          if (subscriptionItem) {
            return subscriptionItem;
          }
        }

        if (state === "one-time") {
          const oneTimeItem = cart.items.find(
            (item) =>
              String(item.variant_id) === String(variantId) &&
              !item.selling_plan_allocation,
          );

          if (oneTimeItem) {
            return oneTimeItem;
          }
        }

        return (
          cart.items.find(
            (item) => String(item.variant_id) === String(variantId),
          ) || null
        );
      }
    }

    return null;
  };

  /**
   * ----------------------------------------------------------
   * Change REAL selling plan
   * ----------------------------------------------------------
   */

  const changeSellingPlan = async (itemKey, sellingPlanId) => {
    if (!itemKey) {
      return;
    }

    const component = [
      ...document.querySelectorAll("[data-minicart-subscription]"),
    ].find(
      (element) =>
        String(element.getAttribute("data-item-key")) === String(itemKey),
    );

    if (!component) {
      console.warn("[Minicart subscriptions] component not found", itemKey);

      scheduleSubscriptionSync();

      return;
    }

    if (component.classList.contains("is-loading")) {
      return;
    }

    component.classList.add("is-loading");

    try {
      const cart = await getLatestCart();

      const item = findCurrentCartItem(cart, itemKey, component);

      if (!item) {
        throw new Error("Cart line not found");
      }

      const targetSellingPlan = sellingPlanId ? Number(sellingPlanId) : null;

      /**
       * Don't perform a useless request.
       */

      const currentSellingPlan =
        Number(item.selling_plan_allocation?.selling_plan?.id) || null;

      if (currentSellingPlan === targetSellingPlan) {
        component.classList.remove("is-loading");

        scheduleSubscriptionSync(50);

        return;
      }

      const response = await fetch(`${getRoot()}cart/change.js`, {
        method: "POST",

        credentials: "same-origin",

        headers: {
          "Content-Type": "application/json",

          Accept: "application/json",
        },

        body: JSON.stringify({
          id: item.key,

          quantity: item.quantity,

          selling_plan: targetSellingPlan,
        }),
      });

      if (!response.ok) {
        throw new Error(`Cart change failed: ${response.status}`);
      }

      /**
       * Read response so the request completes before
       * refreshing the minicart.
       */

      await response.json();

      /**
       * Existing Vue minicart store remains the owner
       * of the normal cart rendering.
       */

      if (
        window.VueMinicart &&
        window.VueMinicart.config?.globalProperties?.$store
      ) {
        try {
          await window.VueMinicart.config.globalProperties.$store.dispatch(
            "initCart",
          );
        } catch {
          scheduleSubscriptionSync(100);
        }
      } else {
        scheduleSubscriptionSync(100);
      }
    } catch (error) {
      console.error("[Minicart subscriptions] change plan Error:", error);

      component.classList.remove("is-loading");

      scheduleSubscriptionSync(100);
    }
  };

  /**
   * ----------------------------------------------------------
   * Mobile selector
   * ----------------------------------------------------------
   */

  const closeAllMobileSubscriptionSelectors = (except = null) => {
    document
      .querySelectorAll("[data-mobile-subscription-select]")
      .forEach((selector) => {
        if (selector === except) {
          return;
        }

        const trigger = selector.querySelector(
          "[data-mobile-subscription-trigger]",
        );

        const dropdown = selector.querySelector(
          "[data-mobile-subscription-dropdown]",
        );

        if (trigger) {
          trigger.setAttribute("aria-expanded", "false");
        }

        if (dropdown) {
          dropdown.hidden = true;
        }

        selector.classList.remove("is-open");
      });
  };

  /**
   * ----------------------------------------------------------
   * Mobile selector click
   * ----------------------------------------------------------
   */

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-mobile-subscription-trigger]");

    if (trigger) {
      const selector = trigger.closest("[data-mobile-subscription-select]");

      if (!selector) {
        return;
      }

      const dropdown = selector.querySelector(
        "[data-mobile-subscription-dropdown]",
      );

      if (!dropdown) {
        return;
      }

      const isOpen = trigger.getAttribute("aria-expanded") === "true";

      closeAllMobileSubscriptionSelectors(selector);

      if (isOpen) {
        trigger.setAttribute("aria-expanded", "false");

        dropdown.hidden = true;

        selector.classList.remove("is-open");

        return;
      }

      trigger.setAttribute("aria-expanded", "true");

      dropdown.hidden = false;

      selector.classList.add("is-open");

      return;
    }

    const option = event.target.closest("[data-mobile-selling-plan]");

    if (option) {
      const selector = option.closest("[data-mobile-subscription-select]");

      if (!selector) {
        return;
      }

      const component = selector.closest("[data-minicart-subscription]");

      if (!component) {
        return;
      }

      const planId = option.getAttribute("data-mobile-selling-plan");

      if (!planId) {
        return;
      }

      const nativeSelect = component.querySelector(
        "[data-subscription-cadence]",
      );

      if (!nativeSelect) {
        return;
      }

      nativeSelect.value = planId;

      const valueElement = selector.querySelector(
        "[data-mobile-subscription-value]",
      );

      if (valueElement) {
        valueElement.textContent = option.textContent.trim();
      }

      selector
        .querySelectorAll("[data-mobile-selling-plan]")
        .forEach((item) => {
          const isSelected = item === option;

          item.classList.toggle("is-selected", isSelected);

          item.setAttribute("aria-selected", String(isSelected));
        });

      const trigger = selector.querySelector(
        "[data-mobile-subscription-trigger]",
      );

      const dropdown = selector.querySelector(
        "[data-mobile-subscription-dropdown]",
      );

      if (trigger) {
        trigger.setAttribute("aria-expanded", "false");
      }

      if (dropdown) {
        dropdown.hidden = true;
      }

      selector.classList.remove("is-open");

      nativeSelect.dispatchEvent(
        new Event("change", {
          bubbles: true,
        }),
      );

      return;
    }

    if (!event.target.closest("[data-mobile-subscription-select]")) {
      closeAllMobileSubscriptionSelectors();
    }
  });

  /**
   * ----------------------------------------------------------
   * Escape
   * ----------------------------------------------------------
   */

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") {
      return;
    }

    closeAllMobileSubscriptionSelectors();
  });

  /**
   * ----------------------------------------------------------
   * Subscription actions
   * ----------------------------------------------------------
   */

  document.addEventListener("click", (event) => {
    const actionElement = event.target.closest("[data-subscription-action]");

    if (!actionElement) {
      return;
    }

    const component = actionElement.closest("[data-minicart-subscription]");

    if (!component) {
      return;
    }

    const itemKey = component.getAttribute("data-item-key");

    const action = actionElement.getAttribute("data-subscription-action");

    /**
     * Switch to one-time.
     */

    if (action === "one-time") {
      changeSellingPlan(itemKey, null);

      return;
    }

    /**
     * Activate subscription.
     */

    if (action === "subscribe") {
      const sellingPlanId = actionElement.getAttribute("data-selling-plan-id");

      if (!sellingPlanId) {
        return;
      }

      changeSellingPlan(itemKey, sellingPlanId);
    }
  });

  /**
   * ----------------------------------------------------------
   * Frequency selector
   * ----------------------------------------------------------
   */

  document.addEventListener("change", (event) => {
    const select = event.target.closest("[data-subscription-cadence]");

    if (!select) {
      return;
    }

    const component = select.closest("[data-minicart-subscription]");

    if (!component) {
      return;
    }

    const itemKey = component.getAttribute("data-item-key");

    const sellingPlanId = select.value;

    if (!sellingPlanId) {
      return;
    }

    changeSellingPlan(itemKey, sellingPlanId);
  });

  /**
   * ----------------------------------------------------------
   * PREPARE CHECKOUT
   * ----------------------------------------------------------
   *
   * This is the only place where the simulated 25%
   * becomes REAL.
   *
   * Example:
   *
   * Cart:
   *
   * Product A -> real 20%
   * Product B -> real 20%
   *
   * Total subscriptions = 2
   *
   * Product B has a 25% plan.
   *
   * UI:
   *
   * Product B = 25%
   *
   * Before checkout:
   *
   * Product B is still REAL 20% in Shopify cart.
   *
   * Click checkout:
   *
   * Product B -> REAL 25%
   *
   * Then redirect.
   */

  const prepareCheckout = async () => {
    if (checkoutPreparing) {
      return false;
    }

    checkoutPreparing = true;

    const checkoutButton = document.querySelector(".minicart__checkout");

    if (checkoutButton) {
      checkoutButton.classList.add("is-loading");

      checkoutButton.setAttribute("aria-busy", "true");
    }

    try {
      let cart = await getLatestCart();

      let cartItems = cart?.items || [];

      const totalSubscriptionUnits = getSubscriptionUnitCount(cartItems);

      /**
       * If fewer than 2 subscription units,
       * nothing can receive 25%.
       */

      if (totalSubscriptionUnits < 2) {
        return true;
      }

      /**
       * Build targets.
       */

      const targets = [];

      /**
       * Fetch all product data in parallel.
       */

      const dataResults = await Promise.all(
        cartItems.map(async (item) => {
          if (!item?.selling_plan_allocation) {
            return {
              item,
              subscriptionData: null,
            };
          }

          return {
            item,
            subscriptionData: await getSubscriptionData(item),
          };
        }),
      );

      dataResults.forEach(({ item, subscriptionData }) => {
        if (!subscriptionData || !item?.selling_plan_allocation) {
          return;
        }

        if (isProductExcludedFrom25(subscriptionData)) {
          return;
        }

        const currentPlan = getCurrentPlan(item, subscriptionData);
        const plan25 = get25Plan(subscriptionData);
        if (!currentPlan || !plan25) {
          return;
        }

        /**
         * Already real 25%.
         */

        if (Number(currentPlan.savingPercentage) === 25) {
          return;
        }

        /**
         * This line qualifies for simulated 25%.
         */

        if (totalSubscriptionUnits >= 2) {
          targets.push({
            originalKey: item.key,

            variantId: item.variant_id,

            quantity: item.quantity,

            targetPlanId: Number(plan25.id),
          });
        }
      });

      if (!targets.length) {
        return true;
      }

      /**
       * ------------------------------------------------------
       * Convert targets one by one.
       * ------------------------------------------------------
       *
       * We refresh the cart after every successful change
       * because Shopify may generate a new line-item key
       * after changing the selling plan.
       */

      for (const target of targets) {
        /**
         * Find the current line.
         *
         * Exact original key first.
         */

        let currentItem = cartItems.find(
          (item) => String(item.key) === String(target.originalKey),
        );

        /**
         * Fallback by variant ID and subscription state.
         */

        if (!currentItem) {
          currentItem = cartItems.find(
            (item) =>
              String(item.variant_id) === String(target.variantId) &&
              !!item.selling_plan_allocation,
          );
        }

        if (!currentItem) {
          console.warn(
            "[Minicart subscriptions] checkout line not found",
            target,
          );

          continue;
        }

        const currentPlanId =
          Number(currentItem.selling_plan_allocation?.selling_plan?.id) || null;

        if (currentPlanId === Number(target.targetPlanId)) {
          continue;
        }

        const response = await fetch(`${getRoot()}cart/change.js`, {
          method: "POST",

          credentials: "same-origin",

          headers: {
            "Content-Type": "application/json",

            Accept: "application/json",
          },

          body: JSON.stringify({
            id: currentItem.key,

            quantity: currentItem.quantity,

            selling_plan: Number(target.targetPlanId),
          }),
        });

        if (!response.ok) {
          throw new Error(`Checkout cart change failed: ${response.status}`);
        }

        /**
         * IMPORTANT:
         *
         * Use Shopify's returned cart.
         * Do NOT use the old line key after this.
         */

        cart = await response.json();

        cartItems = cart?.items || [];
      }

      return true;
    } catch (error) {
      console.error("[Minicart subscriptions] prepare checkout Error:", error);

      return false;
    } finally {
      checkoutPreparing = false;

      if (checkoutButton) {
        checkoutButton.classList.remove("is-loading");

        checkoutButton.removeAttribute("aria-busy");
      }
    }
  };

  /**
   * ----------------------------------------------------------
   * Checkout button
   * ----------------------------------------------------------
   *
   * Capture phase so we control the navigation before the
   * default href behavior.
   */

  document.addEventListener(
    "click",
    async (event) => {
      const checkoutButton = event.target.closest("a.minicart__checkout");

      if (!checkoutButton) {
        return;
      }

      if (checkoutButton.hasAttribute("disabled")) {
        return;
      }

      /**
       * Stop the original navigation.
       */

      event.preventDefault();
      event.stopPropagation();

      if (checkoutPreparing) {
        return;
      }

      const success = await prepareCheckout();

      if (!success) {
        return;
      }

      /**
       * Now Shopify contains the REAL selling plan
       * for every eligible 25% line.
       */

      window.location.assign("/checkout");
    },
    true,
  );

  /**
   * ----------------------------------------------------------
   * Cart lifecycle
   * ----------------------------------------------------------
   */

  document.addEventListener("cart.initCart", () => {
    scheduleSubscriptionSync(100);
  });

  document.addEventListener("cart.addItem", () => {
    scheduleSubscriptionSync(100);
  });

  document.addEventListener("cart.updateItem", () => {
    scheduleSubscriptionSync(100);
  });

  /**
   * ----------------------------------------------------------
   * Shopify standard cart events
   * ----------------------------------------------------------
   *
   * Newer Shopify themes can expose these events.
   * They are harmless if the theme doesn't use them.
   */

  document.addEventListener("shopify:cart:updated", () => {
    scheduleSubscriptionSync(100);
  });

  document.addEventListener("shopify:cart:lines-update", () => {
    scheduleSubscriptionSync(100);
  });

  /**
   * ----------------------------------------------------------
   * Initial load
   * ----------------------------------------------------------
   */

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        scheduleSubscriptionSync(100);
      },
      {
        once: true,
      },
    );
  } else {
    scheduleSubscriptionSync(100);
  }
})();